import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { jobAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Briefcase,
  Plus,
  MoreVertical,
  Edit,
  Trash2,
  ExternalLink,
  Search,
  Filter,
  Loader2,
  Building2,
  Calendar,
} from 'lucide-react';
import { formatDate, getStatusColor, capitalizeFirst } from '../lib/utils';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';

const Jobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    company: '',
    position: '',
    status: 'applied',
    url: '',
    salary: '',
    notes: '',
    applied_date: '',
    interview_date: '',
  });

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await jobAPI.getAll();
        setJobs(response.data);
      } catch (error) {
        toast.error('Failed to load jobs');
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, []);

  const handleOpenDialog = (job = null) => {
    if (job) {
      setEditingJob(job);
      setFormData({
        company: job.company,
        position: job.position,
        status: job.status,
        url: job.url || '',
        salary: job.salary || '',
        notes: job.notes || '',
        applied_date: job.applied_date || '',
        interview_date: job.interview_date || '',
      });
    } else {
      setEditingJob(null);
      setFormData({
        company: '',
        position: '',
        status: 'applied',
        url: '',
        salary: '',
        notes: '',
        applied_date: new Date().toISOString().split('T')[0],
        interview_date: '',
      });
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.company || !formData.position) {
      toast.error('Please fill in company and position');
      return;
    }

    setSaving(true);
    try {
      if (editingJob) {
        const response = await jobAPI.update(editingJob.id, formData);
        setJobs(jobs.map((j) => (j.id === editingJob.id ? response.data : j)));
        toast.success('Application updated!');
      } else {
        const response = await jobAPI.create(formData);
        setJobs([response.data, ...jobs]);
        toast.success('Application added!');
      }
      setShowDialog(false);
    } catch (error) {
      toast.error('Failed to save application');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this application?')) return;

    try {
      await jobAPI.delete(id);
      setJobs(jobs.filter((j) => j.id !== id));
      toast.success('Application deleted');
    } catch (error) {
      toast.error('Failed to delete application');
    }
  };

  const handleStatusChange = async (job, newStatus) => {
    try {
      const response = await jobAPI.update(job.id, { status: newStatus });
      setJobs(jobs.map((j) => (j.id === job.id ? response.data : j)));
      toast.success(`Status updated to ${capitalizeFirst(newStatus)}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const filteredJobs = jobs.filter((job) => {
    const matchesSearch =
      job.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.position.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || job.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const statusOptions = ['applied', 'interview', 'offer', 'rejected'];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="jobs-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Job Tracker
            </h1>
            <p className="text-slate-600 mt-1">
              Track and manage your job applications
            </p>
          </div>
          <Button
            className="bg-indigo-600 hover:bg-indigo-700"
            onClick={() => handleOpenDialog()}
            data-testid="add-job-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Application
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by company or position..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full sm:w-[180px]" data-testid="filter-status">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {statusOptions.map((status) => (
                <SelectItem key={status} value={status}>
                  {capitalizeFirst(status)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Jobs List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : filteredJobs.length > 0 ? (
          <div className="space-y-4">
            {filteredJobs.map((job) => (
              <Card key={job.id} className="card-hover" data-testid={`job-card-${job.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-6 h-6 text-slate-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-semibold text-lg text-slate-900">
                            {job.position}
                          </h3>
                          <p className="text-slate-600">{job.company}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Select
                            value={job.status}
                            onValueChange={(value) => handleStatusChange(job, value)}
                          >
                            <SelectTrigger className={`w-[130px] border ${getStatusColor(job.status)}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {statusOptions.map((status) => (
                                <SelectItem key={status} value={status}>
                                  {capitalizeFirst(status)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => handleOpenDialog(job)}
                                className="cursor-pointer"
                              >
                                <Edit className="w-4 h-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              {job.url && (
                                <DropdownMenuItem asChild>
                                  <a
                                    href={job.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center cursor-pointer"
                                  >
                                    <ExternalLink className="w-4 h-4 mr-2" />
                                    Open Link
                                  </a>
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                onClick={() => handleDelete(job.id)}
                                className="text-red-600 cursor-pointer"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-500">
                        {job.salary && (
                          <span className="flex items-center gap-1">
                            💰 {job.salary}
                          </span>
                        )}
                        {job.applied_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            Applied {formatDate(job.applied_date)}
                          </span>
                        )}
                        {job.interview_date && (
                          <span className="flex items-center gap-1 text-purple-600">
                            <Calendar className="w-4 h-4" />
                            Interview {formatDate(job.interview_date)}
                          </span>
                        )}
                      </div>
                      {job.notes && (
                        <p className="mt-3 text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
                          {job.notes}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Briefcase className="w-10 h-10 text-slate-400" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">
              {searchTerm || filterStatus !== 'all'
                ? 'No matching applications'
                : 'No applications yet'}
            </h2>
            <p className="text-slate-600 mb-6">
              {searchTerm || filterStatus !== 'all'
                ? 'Try adjusting your filters'
                : 'Start tracking your job search today'}
            </p>
            {!searchTerm && filterStatus === 'all' && (
              <Button
                className="bg-indigo-600 hover:bg-indigo-700"
                onClick={() => handleOpenDialog()}
                data-testid="add-first-job-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Application
              </Button>
            )}
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="sm:max-w-[500px]" data-testid="job-dialog" aria-describedby="job-dialog-description">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                {editingJob ? 'Edit Application' : 'Add New Application'}
              </DialogTitle>
              <p id="job-dialog-description" className="text-sm text-slate-500">
                {editingJob ? 'Update the details of your job application' : 'Track a new job application'}
              </p>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Company *</Label>
                  <Input
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    placeholder="Google"
                    data-testid="input-company"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Position *</Label>
                  <Input
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    placeholder="Software Engineer"
                    data-testid="input-position"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger data-testid="select-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map((status) => (
                        <SelectItem key={status} value={status}>
                          {capitalizeFirst(status)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Salary</Label>
                  <Input
                    value={formData.salary}
                    onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                    placeholder="$100,000 - $120,000"
                    data-testid="input-salary"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Job URL</Label>
                <Input
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder="https://careers.google.com/..."
                  data-testid="input-url"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Applied Date</Label>
                  <Input
                    type="date"
                    value={formData.applied_date}
                    onChange={(e) => setFormData({ ...formData, applied_date: e.target.value })}
                    data-testid="input-applied-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Interview Date</Label>
                  <Input
                    type="date"
                    value={formData.interview_date}
                    onChange={(e) => setFormData({ ...formData, interview_date: e.target.value })}
                    data-testid="input-interview-date"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Add any notes about this application..."
                  rows={3}
                  data-testid="input-notes"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="save-job-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                {editingJob ? 'Update' : 'Add'} Application
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default Jobs;
